/**
 * KAN-989: покупка тарифа с кошелька кабинета.
 *
 * Кнопка "Подключить" не уводит на ЮKassa: сначала окно подтверждения
 * (сумма и остаток кошелька), затем POST subscribe. После покупки обновляются
 * подписка, кошелёк и функции тарифа (/auth/me).
 *
 * Логика здесь, компоненты витрины только рендерят состояние хука.
 */
import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { voiceBillingApi } from '@/services/api/voiceBilling';
import { WALLET_QUERY_KEY } from '@/services/api/queryKeys';
import type { OrganizationSubscription } from '@/types/subscription';
import type { TariffPlan } from '@/types/voiceBilling';

/** Ключ запроса действующей подписки (CurrentTariff, PaymentSuccessPage). */
export const CURRENT_SUBSCRIPTION_QUERY_KEY = ['current-subscription'] as const;

/** Код ответа 402 при нехватке денег в кошельке. */
export const INSUFFICIENT_FUNDS_CODE = 'insufficient_funds';

export interface TariffPurchaseError {
  /** Текст для показа: с сервера, если он его прислал. */
  message: string;
  /** Не хватило денег: показать кнопку пополнения. */
  insufficientFunds: boolean;
}

const FALLBACK_MESSAGE = 'Не удалось подключить тариф. Попробуйте ещё раз.';

interface ApiErrorShape {
  response?: { status?: number; data?: { detail?: unknown; code?: unknown } };
}

/**
 * Разбор ошибки покупки. Текст отказа приходит с бэкенда (с именем тарифа,
 * датой, суммами): своя формулировка однажды разошлась бы с правилом сервера.
 */
export function parseTariffPurchaseError(err: unknown): TariffPurchaseError {
  const response =
    typeof err === 'object' && err !== null ? (err as ApiErrorShape).response : undefined;
  const data = response?.data;
  const detail = typeof data?.detail === 'string' && data.detail ? data.detail : null;
  const insufficientFunds = response?.status === 402 || data?.code === INSUFFICIENT_FUNDS_CODE;
  return { message: detail ?? FALLBACK_MESSAGE, insufficientFunds };
}

/** Мутация покупки тарифа с кошелька и обновление зависимых данных. */
export function useSubscribe() {
  const queryClient = useQueryClient();
  return useMutation<OrganizationSubscription, unknown, string>({
    mutationFn: (planId: string) => voiceBillingApi.subscribe(planId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CURRENT_SUBSCRIPTION_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: WALLET_QUERY_KEY });
      // Функции тарифа приходят в /auth/me (KAN-988).
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      queryClient.invalidateQueries({ queryKey: ['currentSubscription'] });
      queryClient.invalidateQueries({ queryKey: ['subscriptionUsage'] });
    },
  });
}

/**
 * Сценарий витрины: выбрать тариф, подтвердить списание, купить.
 * Ошибка живёт до следующей попытки.
 */
export function useTariffPurchaseFlow() {
  const subscribe = useSubscribe();
  const [confirmPlan, setConfirmPlan] = useState<TariffPlan | null>(null);
  const [error, setError] = useState<TariffPurchaseError | null>(null);
  const [purchasedPlanName, setPurchasedPlanName] = useState<string | null>(null);

  const requestPurchase = useCallback((plan: TariffPlan) => {
    setError(null);
    setPurchasedPlanName(null);
    setConfirmPlan(plan);
  }, []);

  const cancelPurchase = useCallback(() => {
    setConfirmPlan(null);
  }, []);

  const { mutate } = subscribe;
  const confirmPurchase = useCallback(() => {
    if (!confirmPlan) return;
    const plan = confirmPlan;
    mutate(plan.id, {
      onSuccess: () => setPurchasedPlanName(plan.name),
      onError: (err) => setError(parseTariffPurchaseError(err)),
      onSettled: () => setConfirmPlan(null),
    });
  }, [confirmPlan, mutate]);

  return {
    confirmPlan,
    requestPurchase,
    cancelPurchase,
    confirmPurchase,
    isPending: subscribe.isPending,
    pendingPlanId: subscribe.isPending ? subscribe.variables ?? null : null,
    error,
    purchasedPlanName,
  };
}
